import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import useLocalStorage from '../hooks/useLocalStorage';
import { KEYS } from '../utils/storage';
import {
  mockRoutes, mockVehicles, mockNotifications, mockHomeworkCompletions,
} from '../data/mockData';

const AppContext = createContext(null);

// Diff two arrays and sync changes to API
async function syncDiff(apiPath, prev, next) {
  const prevMap = new Map((prev || []).map(x => [x.id, x]));
  const nextMap = new Map((next || []).map(x => [x.id, x]));

  for (const [id, item] of nextMap) {
    if (!prevMap.has(id)) {
      await api.create(apiPath, item);
    } else if (JSON.stringify(prevMap.get(id)) !== JSON.stringify(item)) {
      await api.update(apiPath, id, item);
    }
  }
  for (const [id] of prevMap) {
    if (!nextMap.has(id)) {
      await api.remove(apiPath, id);
    }
  }
}

export function AppProvider({ children }) {
  const [loading, setLoading]   = useState(true);
  const [apiReady, setApiReady] = useState(false);
  const apiReadyRef = useRef(false);

  // API-backed state
  const [students,      setStudentsRaw]      = useState([]);
  const [parents,       setParentsRaw]       = useState([]);
  const [staff,         setStaffRaw]         = useState([]);
  const [classes,       setClassesRaw]       = useState([]);
  const [subjects,      setSubjectsRaw]      = useState([]);
  const [timetable,     setTimetableRaw]     = useState([]);
  const [exams,         setExamsRaw]         = useState([]);
  const [marks,         setMarksRaw]         = useState([]);
  const [attendance,    setAttendanceRaw]    = useState([]);
  const [feeStructures, setFeeStructuresRaw] = useState([]);
  const [feePayments,   setFeePaymentsRaw]   = useState([]);
  const [books,         setBooksRaw]         = useState([]);
  const [bookIssues,    setBookIssuesRaw]    = useState([]);
  const [announcements, setAnnouncementsRaw] = useState([]);
  const [homework,      setHomeworkRaw]      = useState([]);
  const [examTimetable, setExamTimetableRaw] = useState([]);
  const [lessonPlans,   setLessonPlansRaw]   = useState([]);
  const [enquiries,     setEnquiriesRaw]     = useState([]);

  // Local-only (no backend table)
  const [routes,        setRoutes]        = useLocalStorage(KEYS.ROUTES,        mockRoutes);
  const [vehicles,      setVehicles]      = useLocalStorage(KEYS.VEHICLES,      mockVehicles);
  const [notifications, setNotifications] = useLocalStorage(KEYS.NOTIFICATIONS, mockNotifications);
  const [hwCompletions, setHwCompletions] = useLocalStorage(KEYS.HW_COMPLETIONS, mockHomeworkCompletions);

  // Load all data from backend on mount
  useEffect(() => {
    async function loadAll() {
      try {
        const [s, p, st, cl, sub, tt, ex, mk, att, fs, fp, bk, bi, ann, hw, et, lp, enq] =
          await Promise.all([
            api.list('students'),
            api.list('parents'),
            api.list('staff'),
            api.list('classes'),
            api.list('subjects'),
            api.list('timetable'),
            api.list('exams'),
            api.list('marks'),
            api.list('attendance'),
            api.list('fee-structures'),
            api.list('fee-payments'),
            api.list('books'),
            api.list('book-issues'),
            api.list('announcements'),
            api.list('homework'),
            api.list('exam-timetable'),
            api.list('lesson-plans'),
            api.list('enquiries'),
          ]);
        setStudentsRaw(s);      setParentsRaw(p);       setStaffRaw(st);
        setClassesRaw(cl);      setSubjectsRaw(sub);    setTimetableRaw(tt);
        setExamsRaw(ex);        setMarksRaw(mk);        setAttendanceRaw(att);
        setFeeStructuresRaw(fs);setFeePaymentsRaw(fp);
        setBooksRaw(bk);        setBookIssuesRaw(bi);
        setAnnouncementsRaw(ann);setHomeworkRaw(hw);
        setExamTimetableRaw(et);setLessonPlansRaw(lp);  setEnquiriesRaw(enq);
        apiReadyRef.current = true;
        setApiReady(true);
      } catch (e) {
        console.warn('Backend not reachable, running in offline mode:', e.message);
        apiReadyRef.current = false;
        setApiReady(false);
      }
      setLoading(false);
    }
    loadAll();
  }, []);

  // Factory: wrap a raw setState so writes also sync to the backend
  function makeSetter(rawSetter, apiPath) {
    return (updater) => {
      rawSetter(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (apiReadyRef.current) {
          syncDiff(apiPath, prev, next).catch(err =>
            console.error(`Sync error [${apiPath}]:`, err.message)
          );
        }
        return next;
      });
    };
  }

  const setStudents      = makeSetter(setStudentsRaw,      'students');
  const setParents       = makeSetter(setParentsRaw,       'parents');
  const setStaff         = makeSetter(setStaffRaw,         'staff');
  const setClasses       = makeSetter(setClassesRaw,       'classes');
  const setSubjects      = makeSetter(setSubjectsRaw,      'subjects');
  const setTimetable     = makeSetter(setTimetableRaw,     'timetable');
  const setExams         = makeSetter(setExamsRaw,         'exams');
  const setMarks         = makeSetter(setMarksRaw,         'marks');
  const setAttendance    = makeSetter(setAttendanceRaw,    'attendance');
  const setFeeStructures = makeSetter(setFeeStructuresRaw, 'fee-structures');
  const setFeePayments   = makeSetter(setFeePaymentsRaw,   'fee-payments');
  const setBooks         = makeSetter(setBooksRaw,         'books');
  const setBookIssues    = makeSetter(setBookIssuesRaw,    'book-issues');
  const setAnnouncements = makeSetter(setAnnouncementsRaw, 'announcements');
  const setHomework      = makeSetter(setHomeworkRaw,      'homework');
  const setExamTimetable = makeSetter(setExamTimetableRaw, 'exam-timetable');
  const setLessonPlans   = makeSetter(setLessonPlansRaw,   'lesson-plans');
  const setEnquiries     = makeSetter(setEnquiriesRaw,     'enquiries');

  return (
    <AppContext.Provider value={{
      loading, apiReady,
      students,      setStudents,
      parents,       setParents,
      staff,         setStaff,
      classes,       setClasses,
      subjects,      setSubjects,
      timetable,     setTimetable,
      exams,         setExams,
      marks,         setMarks,
      attendance,    setAttendance,
      feeStructures, setFeeStructures,
      feePayments,   setFeePayments,
      books,         setBooks,
      bookIssues,    setBookIssues,
      routes,        setRoutes,
      vehicles,      setVehicles,
      announcements, setAnnouncements,
      homework,      setHomework,
      notifications, setNotifications,
      hwCompletions, setHwCompletions,
      examTimetable, setExamTimetable,
      lessonPlans,   setLessonPlans,
      enquiries,     setEnquiries,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
